import parse, { domToReact } from 'html-react-parser';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

/**
 * Leading slash, no trailing slash, so prefix checks do not match a longer segment.
 */
const portalBasePath = (basename) => {
  if (!basename || typeof basename !== 'string') return '';
  const withLeadingSlash = basename.startsWith('/') ? basename : `/${basename}`;
  return withLeadingSlash.replace(/\/+$/, '');
};

/**
 * Coverts a normal CSS string into a JSON object representing the CSS.
 * This is because React expects a JSON object.
 * @param styleString
 * @return css object
 */
const convertCssToObject = (styleString) =>
  styleString
    .split(';')
    .map((style) => style.split(':').map((x) => x.replace(/['" ]/g, '')))
    .reduce((cssObj, [key, value]) => {
      cssObj[key] = value;
      return cssObj;
    }, {});

/**
 * Replace internal <a> tags with <Link> tags with additional Breadcrumbs info.
 * Needed for proper Breadcrumb rendering, and preventing of page reload on the Single Page Application.
 */
const parseAnchor = ({ attribs, children }, breadCrumbs, basename) => {
  if (!attribs || !Object.hasOwn(attribs, 'href')) return;
  let [path, params] = attribs.href.split('?');

  const basePath = portalBasePath(basename);
  const isSpaLink = basePath !== '' && (path === basePath || path.startsWith(`${basePath}/`));

  if (!isSpaLink) {
    return <a {...attribs}>{domToReact(children)}</a>;
  }

  path = path.slice(basePath.length) || '/';

  delete attribs.href;
  attribs.className = attribs.class; // React expects class attribute as className
  delete attribs.class;
  if (attribs.style) {
    // Convert style attribute to React format
    attribs.style = convertCssToObject(attribs.style);
  }

  return (
    <Link {...attribs} to={{ pathname: path, search: params }} state={{ breadCrumbs: breadCrumbs }}>
      {domToReact(children)}
    </Link>
  );
};

/**
 * Replaces <a> in the given html with <Link> tags with breadcrumbs attribute.
 * Also ensures that onclick listeners aren't overwritten.
 * @param html
 * @param breadCrumbs
 * @return {ReturnType<typeof domToReact>}
 */
const parseHtml = (html, breadCrumbs, basename) =>
  html &&
  typeof html === 'string' &&
  parse(html, {
    replace: (domElement) => (domElement.name === 'a' ? parseAnchor(domElement, breadCrumbs, basename) : null),
  });

/**
 * Returns a div with the given CSS class, and the given inner HTML.
 * Note: <a> tags are replaced react-router-do <Link> tags with breadcrumb info added.
 * @param value The inner HTML
 * @param cssClass The class name of the outer <div>
 * @param breadCrumbs The breadcrumbs of the current page
 * @return {*}
 * @constructor
 */
const HTMLDisplay = ({ value, cssClass, breadCrumbs }) => {
  const basename = useSelector((state) => state.config.basename);
  return <div className={cssClass}>{parseHtml(value, breadCrumbs, basename)}</div>;
};

HTMLDisplay.propTypes = {
  code: PropTypes.string,
  css: PropTypes.string,
};

export default HTMLDisplay;
